import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { formatCurrency, formatNumber } from '../../../../utils/formatting';
import { STATEROOM_TYPES } from '../../../../constants/stateroom';
import { LOW_BERTH_SPEC, EMERGENCY_LOW_BERTH_SPEC, COMMON_AREA_SPEC } from '../../../../constants/stateroom';
import { setStateroomConfig } from '../../../../store/features/shipBuilderSlice';

interface BreakawayStateroomConfigProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SectionStateroomAssignment {
  sectionNumber: number;
  sectionName: string;
  sectionSize: number;
  singleStaterooms: number;
  doubleStaterooms: number;
  lowBerths: number;
  emergencyLowBerths: number;
  commonArea: number;
}

export const BreakawayStateroomConfig: React.FC<BreakawayStateroomConfigProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const hull = useAppSelector((state) => state.shipBuilder.hull);
  const stateroomConfig = useAppSelector((state) => state.shipBuilder.stateroomConfig);
  
  const [sectionAssignments, setSectionAssignments] = useState<SectionStateroomAssignment[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize assignments from existing stateroom configuration
  useEffect(() => {
    if (hull?.breakawayConfig?.sections) {
      const newAssignments: SectionStateroomAssignment[] = [];

      // Calculate total stateroom requirements for the whole ship
      const totalSingleStaterooms = stateroomConfig?.single || 0;
      const totalDoubleStaterooms = stateroomConfig?.double || 0;
      const totalLowBerths = stateroomConfig?.lowBerths?.count || 0;
      const totalEmergencyLowBerths = stateroomConfig?.emergencyLowBerths?.count || 0;
      const totalCommonArea = stateroomConfig?.commonArea || 0;

      // Distribute staterooms proportionally across sections based on section size
      const totalShipSize = hull.breakawayConfig.sections.reduce((total, section) => total + section.size, 0);

      hull.breakawayConfig.sections.forEach(section => {
        const sectionRatio = section.size / totalShipSize;
        
        newAssignments.push({
          sectionNumber: section.sectionNumber,
          sectionName: section.name || `Section ${section.sectionNumber}`,
          sectionSize: section.size,
          singleStaterooms: Math.floor(totalSingleStaterooms * sectionRatio),
          doubleStaterooms: Math.floor(totalDoubleStaterooms * sectionRatio),
          lowBerths: Math.floor(totalLowBerths * sectionRatio),
          emergencyLowBerths: Math.floor(totalEmergencyLowBerths * sectionRatio),
          commonArea: Math.floor(totalCommonArea * sectionRatio),
        });
      });

      setSectionAssignments(newAssignments);
    }
  }, [hull?.breakawayConfig?.sections, stateroomConfig]);

  const handleAssignmentChange = (sectionNumber: number, field: keyof Omit<SectionStateroomAssignment, 'sectionNumber' | 'sectionName' | 'sectionSize'>, value: number) => {
    setSectionAssignments(prev => 
      prev.map(assignment => 
        assignment.sectionNumber === sectionNumber
          ? { ...assignment, [field]: Math.max(0, value) }
          : assignment
      )
    );
    setErrors(prev => ({ ...prev, [`${sectionNumber}-${field}`]: '' }));
  };

  const getTotalAssigned = (field: keyof Omit<SectionStateroomAssignment, 'sectionNumber' | 'sectionName' | 'sectionSize'>) => {
    return sectionAssignments.reduce((sum, assignment) => sum + assignment[field], 0);
  };

  const getOriginalTotal = (field: keyof Omit<SectionStateroomAssignment, 'sectionNumber' | 'sectionName' | 'sectionSize'>) => {
    switch (field) {
      case 'singleStaterooms':
        return stateroomConfig?.single || 0;
      case 'doubleStaterooms':
        return stateroomConfig?.double || 0;
      case 'lowBerths':
        return stateroomConfig?.lowBerths?.count || 0;
      case 'emergencyLowBerths':
        return stateroomConfig?.emergencyLowBerths?.count || 0;
      case 'commonArea':
        return stateroomConfig?.commonArea || 0;
      default:
        return 0;
    }
  };

  const validateAssignments = () => {
    const newErrors: Record<string, string> = {};
    const fields: (keyof Omit<SectionStateroomAssignment, 'sectionNumber' | 'sectionName' | 'sectionSize'>)[] = [
      'singleStaterooms', 'doubleStaterooms', 'lowBerths', 'emergencyLowBerths', 'commonArea'
    ];

    fields.forEach(field => {
      const totalAssigned = getTotalAssigned(field);
      const originalTotal = getOriginalTotal(field);

      if (totalAssigned !== originalTotal) {
        newErrors[`total-${field}`] = `Total ${field} must equal ${originalTotal}. Currently assigned: ${totalAssigned}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProportionalDistribution = () => {
    if (!hull?.breakawayConfig?.sections) return;

    const totalShipSize = hull.breakawayConfig.sections.reduce((total, section) => total + section.size, 0);
    const fields: (keyof Omit<SectionStateroomAssignment, 'sectionNumber' | 'sectionName' | 'sectionSize'>)[] = [
      'singleStaterooms', 'doubleStaterooms', 'lowBerths', 'emergencyLowBerths', 'commonArea'
    ];

    const newAssignments = sectionAssignments.map(assignment => {
      const sectionRatio = assignment.sectionSize / totalShipSize;
      const newAssignment = { ...assignment };

      fields.forEach(field => {
        const originalTotal = getOriginalTotal(field);
        newAssignment[field] = Math.floor(originalTotal * sectionRatio);
      });

      return newAssignment;
    });

    setSectionAssignments(newAssignments);
    setErrors({});
  };

  const handleSave = () => {
    if (!validateAssignments()) {
      return;
    }

    // Calculate totals
    const totalSingle = getTotalAssigned('singleStaterooms');
    const totalDouble = getTotalAssigned('doubleStaterooms');
    const totalLowBerths = getTotalAssigned('lowBerths');
    const totalEmergencyLowBerths = getTotalAssigned('emergencyLowBerths');
    const totalCommonArea = getTotalAssigned('commonArea');

    // Calculate costs, tons, and power
    const totalCost = 
      totalSingle * STATEROOM_TYPES.STANDARD.cost +
      totalDouble * STATEROOM_TYPES.STANDARD.cost +
      totalLowBerths * LOW_BERTH_SPEC.cost +
      totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.cost +
      totalCommonArea * COMMON_AREA_SPEC.costPerTon;

    const totalTons = 
      totalSingle * STATEROOM_TYPES.STANDARD.tons +
      totalDouble * STATEROOM_TYPES.STANDARD.tons +
      totalLowBerths * LOW_BERTH_SPEC.tons +
      totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.tons +
      totalCommonArea;

    const totalPower = 
      Math.ceil(totalLowBerths / 10) +
      totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.power +
      totalCommonArea * COMMON_AREA_SPEC.powerPerTon;

    // Update the main stateroom configuration with the combined totals
    const combinedConfig = {
      single: totalSingle,
      double: totalDouble,
      lowBerths: {
        count: totalLowBerths,
        cost: totalLowBerths * LOW_BERTH_SPEC.cost,
        tons: totalLowBerths * LOW_BERTH_SPEC.tons,
        power: Math.ceil(totalLowBerths / 10),
        capacity: totalLowBerths,
        techLevel: LOW_BERTH_SPEC.techLevel,
        maintenanceCost: totalLowBerths * LOW_BERTH_SPEC.maintenanceCost,
      },
      emergencyLowBerths: {
        count: totalEmergencyLowBerths,
        cost: totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.cost,
        tons: totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.tons,
        power: totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.power,
        capacity: totalEmergencyLowBerths,
        techLevel: EMERGENCY_LOW_BERTH_SPEC.techLevel,
        maintenanceCost: totalEmergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.maintenanceCost,
      },
      commonArea: totalCommonArea,
      totalCost,
      totalTons,
      totalPower,
      comfort: 0, // Default comfort value
      warnings: [], // No warnings for now
    };

    dispatch(setStateroomConfig(combinedConfig));
    onClose();
  };

  if (!isOpen || !hull?.breakawayConfig?.sections) return null;

  const fields: (keyof Omit<SectionStateroomAssignment, 'sectionNumber' | 'sectionName' | 'sectionSize'>)[] = [
    'singleStaterooms', 'doubleStaterooms', 'lowBerths', 'emergencyLowBerths', 'commonArea'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Breakaway Stateroom Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Breakaway Stateroom Rules</h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Staterooms must be assigned to specific breakaway sections</li>
              <li>• Totals are calculated for the whole ship</li>
              <li>• Each section can have different stateroom configurations</li>
              <li>• Total assignments must match the original ship requirements</li>
            </ul>
          </div>
        </div>

        {/* Proportional Distribution Button */}
        <div className="mb-6">
          <button
            onClick={handleProportionalDistribution}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Distribute Proportionally by Section Size
          </button>
        </div>

        {/* Section Assignment Table */}
        <div className="space-y-6">
          {sectionAssignments.map((section) => (
            <div key={section.sectionNumber} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">
                {section.sectionName} ({section.sectionSize} tons)
              </h4>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Single Staterooms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Single Staterooms
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={section.singleStaterooms}
                    onChange={(e) => handleAssignmentChange(section.sectionNumber, 'singleStaterooms', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formatNumber(section.singleStaterooms * STATEROOM_TYPES.STANDARD.tons)} tons
                  </div>
                </div>

                {/* Double Staterooms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Double Staterooms
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={section.doubleStaterooms}
                    onChange={(e) => handleAssignmentChange(section.sectionNumber, 'doubleStaterooms', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formatNumber(section.doubleStaterooms * STATEROOM_TYPES.STANDARD.tons)} tons
                  </div>
                </div>

                {/* Low Berths */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Low Berths
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={section.lowBerths}
                    onChange={(e) => handleAssignmentChange(section.sectionNumber, 'lowBerths', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formatNumber(section.lowBerths * LOW_BERTH_SPEC.tons)} tons
                  </div>
                </div>

                {/* Emergency Low Berths */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emergency Low Berths
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={section.emergencyLowBerths}
                    onChange={(e) => handleAssignmentChange(section.sectionNumber, 'emergencyLowBerths', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formatNumber(section.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.tons)} tons
                  </div>
                </div>

                {/* Common Area */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Common Area (tons)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={section.commonArea}
                    onChange={(e) => handleAssignmentChange(section.sectionNumber, 'commonArea', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formatNumber(section.commonArea)} tons
                  </div>
                </div>
              </div>

              {/* Section Totals */}
              <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <span className="font-medium">Total Tons:</span> {formatNumber(
                      section.singleStaterooms * STATEROOM_TYPES.STANDARD.tons +
                      section.doubleStaterooms * STATEROOM_TYPES.STANDARD.tons +
                      section.lowBerths * LOW_BERTH_SPEC.tons +
                      section.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.tons +
                      section.commonArea
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Total Cost:</span> {formatCurrency(
                      section.singleStaterooms * STATEROOM_TYPES.STANDARD.cost +
                      section.doubleStaterooms * STATEROOM_TYPES.STANDARD.cost +
                      section.lowBerths * LOW_BERTH_SPEC.cost +
                      section.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.cost +
                      section.commonArea * COMMON_AREA_SPEC.costPerTon
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Total Power:</span> {
                      Math.ceil(section.lowBerths / 10) +
                      section.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.power +
                      section.commonArea * COMMON_AREA_SPEC.powerPerTon
                    }
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Ship-wide Totals */}
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Ship-wide Totals</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            {fields.map(field => {
              const totalAssigned = getTotalAssigned(field);
              const originalTotal = getOriginalTotal(field);
              const isCorrect = totalAssigned === originalTotal;
              
              return (
                <div key={field} className={`p-2 rounded ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="font-medium text-gray-700">{field.replace(/([A-Z])/g, ' $1').trim()}</div>
                  <div className={`font-bold ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                    {totalAssigned} / {originalTotal}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <h4 className="text-sm font-semibold text-red-800 mb-2">Assignment Errors</h4>
            <ul className="text-sm text-red-700 space-y-1">
              {Object.entries(errors).map(([key, error]) => (
                <li key={key}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={Object.keys(errors).length > 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}; 